// SPDX-License-Identifier: AGPL-3.0-or-later

import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import styles from '@app/features/theme/styles/Typing.module.css';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type {CSSProperties} from 'react';
import {useLayoutEffect, useMemo, useRef} from 'react';

const TYPING_INDICATOR_DESCRIPTOR = msg({
	message: 'Typing indicator',
	comment: 'Short label in the channel and chat typing. Keep it concise.',
});

// The dots' 1s steps(8) blink (Typing.module.css) is paused: a running animation, stepped or not, makes the page draw on
// every display refresh. One timer shared by every mounted indicator moves the paused blink to each step, 16 times a
// second; the stylesheet keeps the keyframes, the steps and the dot offsets.
const BLINK_STEP_MS = 500 / 8;

interface BlinkingDots {
	dots: Array<SVGElement>;
	startTick: number;
}

const blinkingDots = new Set<BlinkingDots>();
let blinkOrigin = 0;
let blinkTick = 0;
let blinkTimer: number | null = null;
let blinkRootObserver: MutationObserver | null = null;

// The stylesheet freezes the dots in these states (Typing.module.css, the game mode rules): the clock sleeps until the
// root's classes change.
function blinkPaused(): boolean {
	const root = document.documentElement.classList;
	return (
		root.contains('reduced-motion') ||
		root.contains('game-mode') ||
		root.contains('game-mode-lite') ||
		(!root.contains('window-focused') && !root.contains('unfocused-fully-interactive'))
	);
}

function scheduleBlink(): void {
	blinkTimer = window.setTimeout(
		onBlinkTick,
		Math.max(0, blinkOrigin + (blinkTick + 1) * BLINK_STEP_MS - performance.now()),
	);
}

function wakeBlink(): void {
	if (blinkTimer != null || blinkingDots.size === 0 || blinkPaused()) return;
	blinkTick = Math.floor((performance.now() - blinkOrigin) / BLINK_STEP_MS);
	scheduleBlink();
}

function onBlinkTick(): void {
	blinkTimer = null;
	if (blinkPaused()) return;
	blinkTick = Math.max(blinkTick + 1, Math.round((performance.now() - blinkOrigin) / BLINK_STEP_MS));
	const steps: Array<[Animation, number]> = [];
	for (const entry of blinkingDots) {
		const time = (blinkTick - entry.startTick) * BLINK_STEP_MS;
		for (const dot of entry.dots) {
			for (const animation of dot.getAnimations()) {
				if (animation.playState !== 'idle') steps.push([animation, time]);
			}
		}
	}
	for (const [animation, time] of steps) animation.currentTime = time;
	scheduleBlink();
}

function registerBlinkingDots(dots: Array<SVGElement>): () => void {
	if (blinkingDots.size === 0) {
		blinkOrigin = performance.now();
		blinkTick = 0;
		blinkRootObserver = new MutationObserver(wakeBlink);
		blinkRootObserver.observe(document.documentElement, {attributes: true, attributeFilter: ['class']});
	}
	const entry = {dots, startTick: Math.floor((performance.now() - blinkOrigin) / BLINK_STEP_MS)};
	blinkingDots.add(entry);
	wakeBlink();
	return () => {
		blinkingDots.delete(entry);
		if (blinkingDots.size > 0) return;
		blinkRootObserver?.disconnect();
		blinkRootObserver = null;
		if (blinkTimer == null) return;
		window.clearTimeout(blinkTimer);
		blinkTimer = null;
	};
}

interface TypingProps {
	className?: string;
	size?: number;
	style?: CSSProperties;
	color?: string;
}

export const Typing = observer(
	({className, size = 40, style, color = 'var(--typing-indicator-color, var(--text-chat))'}: TypingProps) => {
		const {i18n} = useLingui();
		const scale = useMemo(() => size / 40, [size]);
		const x = useMemo(() => 3.75 * scale, [scale]);
		const y = useMemo(() => 7.5 * scale, [scale]);
		const width = useMemo(() => 17.5 * scale, [scale]);
		const height = useMemo(() => 5 * scale, [scale]);
		const viewBoxWidth = 20;
		const viewBoxHeight = 5;
		const mergedStyle = useMemo(() => ({...(style || {}), color}), [style, color]);
		const svgRef = useRef<SVGSVGElement>(null);
		useLayoutEffect(() => {
			const svg = svgRef.current;
			if (!svg) return;
			return registerBlinkingDots([...svg.querySelectorAll('circle')]);
		}, []);
		return (
			<svg
				ref={svgRef}
				x={x}
				y={y}
				width={remFromPx(width)}
				height={remFromPx(height)}
				viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
				className={className}
				style={mergedStyle}
				role="img"
				aria-label={i18n._(TYPING_INDICATOR_DESCRIPTOR)}
				data-flx="channel.typing.img"
			>
				<circle cx="2.5" cy="2.5" r="2.5" className={styles.dot} fill={color} data-flx="channel.typing.dot" />
				<circle cx="8.75" cy="2.5" r="2.5" className={styles.dot} fill={color} data-flx="channel.typing.dot--2" />
				<circle cx={15} cy="2.5" r="2.5" className={styles.dot} fill={color} data-flx="channel.typing.dot--3" />
			</svg>
		);
	},
);
